import cashoutBoostCalculationAnimationBRLJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationBRL.json';
import cashoutBoostCalculationAnimationCHFJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationCHF.json';
import cashoutBoostCalculationAnimationEURJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationEUR.json';
import cashoutBoostCalculationAnimationGBPJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationGBP.json';
import cashoutBoostCalculationAnimationINRJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationINR.json';
import cashoutBoostCalculationAnimationUSDJson from '../assets/animations/kwiffed/cashout-boost/cashoutBoostCalculationAnimationUSD.json';
import { AcceptedCountries } from '../data-types/international/internationalEnums';
import { User } from '../data-types/user/userInterfaces';
import { CURRENCY_LIST } from '../data-types/wallet/walletConstants';
import { CURRENCY_ISO, CURRENCY_NAME, CURRENCY_SYMBOL } from '../data-types/wallet/walletEnums';

export const getDefaultCurrencyISOCodeByUserLocation = (userLocation: AcceptedCountries | undefined) => {
  switch (userLocation) {
    case AcceptedCountries.CANADA:
      return CURRENCY_ISO.CANADIAN_DOLLAR;
    case AcceptedCountries.INDIA:
      return CURRENCY_ISO.INDIAN_RUPEE;
    case AcceptedCountries.KENYA:
      return CURRENCY_ISO.KENYAN_SHILLING;
    case AcceptedCountries.NEW_ZEALAND:
      return CURRENCY_ISO.NEW_ZEALAND_DOLLAR;
    case AcceptedCountries.BRAZIL:
      return CURRENCY_ISO.BRAZILIAN_REAL;
    case AcceptedCountries.MEXICO:
    case AcceptedCountries.COLOMBIA:
    case AcceptedCountries.CHILE:
    case AcceptedCountries.PERU:
    case AcceptedCountries.ARGENTINA:
    case AcceptedCountries.ECUADOR:
    case AcceptedCountries.EL_SALVADOR:
    case AcceptedCountries.PARAGUAY:
    case AcceptedCountries.URUGUAY:
      return CURRENCY_ISO.UNITED_STATES_DOLLAR;
    case AcceptedCountries.IRELAND:
    case AcceptedCountries.AUSTRIA:
    case AcceptedCountries.NORWAY:
    case AcceptedCountries.FINLAND:
    case AcceptedCountries.HUNGARY:
    case AcceptedCountries.ISLE_OF_MAN:
    case AcceptedCountries.BOSNIA_AND_HERZEGOVINA:
    case AcceptedCountries.KOSOVO:
    case AcceptedCountries.SERBIA:
    case AcceptedCountries.MONTENEGRO:
    case AcceptedCountries.CROATIA:
    case AcceptedCountries.UNITED_ARAB_EMIRATES:
    case AcceptedCountries.ARMENIA:
    case AcceptedCountries.SLOVAKIA:
    case AcceptedCountries.SLOVENIA:
    case AcceptedCountries.GEORGIA:
      return CURRENCY_ISO.EURO;
    case AcceptedCountries.SWITZERLAND:
      return CURRENCY_ISO.SWISS_FRANC;
    case AcceptedCountries.GREAT_BRITAIN:
    default:
      return CURRENCY_ISO.BRITISH_POUND;
  }
};

export const getCurrencyDetailsByISO = (currencyISO: string) => CURRENCY_LIST.find(currency => currency.currencyISO === currencyISO);

const getCurrencySymbolByISO = (currencyISO: string) => {
  const currency = getCurrencyDetailsByISO(currencyISO);

  return currency ? currency.symbol : '';
};

export const getCurrencyISOCode = (user: User | undefined, userLocation: AcceptedCountries | undefined) => {
  if (user && user.currency && user.currency.isoAlphabeticCode) {
    return user.currency.isoAlphabeticCode;
  }

  return getDefaultCurrencyISOCodeByUserLocation(userLocation);
};

export const getCurrencySymbol = (user: User | undefined, userLocation: AcceptedCountries | undefined) => {
  if (user && user.currency && user.currency.symbol) {
    return user.currency.symbol;
  }

  const defaultCurrencyISOInLocation = getDefaultCurrencyISOCodeByUserLocation(userLocation);

  return getCurrencySymbolByISO(defaultCurrencyISOInLocation);
};

export const getCurrencyNameBySymbol = (currencySymbol: CURRENCY_SYMBOL) => {
  switch (currencySymbol) {
    case CURRENCY_SYMBOL.EURO:
      return CURRENCY_NAME.EURO;
    case CURRENCY_SYMBOL.DOLLAR:
      return CURRENCY_NAME.DOLLAR;
    case CURRENCY_SYMBOL.INDIAN_RUPEE:
      return CURRENCY_NAME.INDIAN_RUPEE;
    case CURRENCY_SYMBOL.KENYAN_SHILLING:
      return CURRENCY_NAME.KENYAN_SHILLING;
    case CURRENCY_SYMBOL.BRAZILIAN_REAL:
      return CURRENCY_NAME.BRAZILIAN_REAL;
    case CURRENCY_SYMBOL.CANADIAN_DOLLAR:
      return CURRENCY_NAME.CANADIAN_DOLLAR;
    case CURRENCY_SYMBOL.NEW_ZEALAND_DOLLAR:
      return CURRENCY_NAME.NEW_ZEALAND_DOLLAR;
    case CURRENCY_SYMBOL.SWISS_FRANC:
      return CURRENCY_NAME.SWISS_FRANC;
    case CURRENCY_SYMBOL.POUND:
    default:
      return CURRENCY_NAME.POUND;
  }
};

export const getCurrencyAnimation = (userCurrency: string) => {
  switch (userCurrency) {
    case CURRENCY_SYMBOL.EURO: {
      return cashoutBoostCalculationAnimationEURJson;
    }
    case CURRENCY_SYMBOL.DOLLAR: {
      return cashoutBoostCalculationAnimationUSDJson;
    }
    case CURRENCY_SYMBOL.BRAZILIAN_REAL: {
      return cashoutBoostCalculationAnimationBRLJson;
    }
    case CURRENCY_SYMBOL.INDIAN_RUPEE: {
      return cashoutBoostCalculationAnimationINRJson;
    }
    case CURRENCY_SYMBOL.SWISS_FRANC: {
      return cashoutBoostCalculationAnimationCHFJson;
    }
    case CURRENCY_SYMBOL.POUND:
    default: {
      return cashoutBoostCalculationAnimationGBPJson;
    }
  }
};

export const getCurrencyDetailsFromISOs = (currencyISOs: string[]) =>
  currencyISOs.map(currencyISO => {
    const currency = getCurrencyDetailsByISO(currencyISO);

    return {
      currencyISO,
      symbol: (currency && currency.symbol) || '',
      nameId: (currency && currency.nameId) || '',
    };
  });
